import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { Icon } from "@material-ui/core";
import { Skeleton } from "@material-ui/lab";

import { auth } from "js/firebase/auth";
import { workspaces as workspacesApi } from "apis/callables";
import {
    ActionPermissionsObject,
    GET_DEFAULT_RESTRICTED_PERMISSIONS_ACCESS,
    IWorkspacePermissionAccess,
    IWorkspaceResourceFolder,
    IWorkspaceResourceFolderMetadata,
    WorkspacePermissionSubjectType,
    WorkspaceResourceFolderType,
    WorkspaceResourceType,
    PermissionActionType,
    WorkspacePlanTier
} from "common/interfaces";
import Banner from "js/Components/Banner";
import { IconButton } from "js/Components/IconButton";
import { List, ListHeaderLine, ListLine } from "js/Components/List";
import { MenuItem } from "js/Components/Menu";
import { Popup, PopupContent, PopupPreview } from "js/Components/Popup";
import WorkspaceController, {
    WorkspaceControllerState,
    getUserPermissionsToResourceFolder
} from "js/controllers/WorkspaceController";
import getLogger from "js/core/logger";
import withProgressDialog from "js/core/utilities/withProgressDialog";
import {
    ShowConfirmationDialog,
    ShowDialog,
    ShowErrorDialog,
    ShowInputDialog,
    ShowWarningDialog
} from "js/react/components/Dialogs/BaseDialog";
import { NoMatchNotice, NoTeamFoldersNotice } from "js/react/components/Notice";
import ChangePlanDialog from "js/react/components/Dialogs/ChangePlanDialog";
import { FlexBox } from "js/react/components/LayoutGrid";
import { BlueButton } from "js/react/components/UiComponents";
import { useIntersectionObserver } from "js/react/hooks/useIntersectionObserver";
import AllTeamMembersFolderSettingsDialog from "js/react/views/TeamResources/dialogs/AllTeamMembersFolderSettingsDialog";
import { TeamResourcesPaneHeader } from "js/react/views/TeamResources/components/TeamResourcesPaneHeader";
import { TeamResourcesResultsContainer, TeamResourcesUIPane } from "js/react/views/TeamResources/components/TeamResourcesPaneLayout";
import { ManageTeamFolderMembersDialog } from "js/react/views/TeamResources/dialogs/TeamFolderDialogs";
import UpgradeIcon from "js/Components/UpgradeIcon";
import { Gap5 } from "js/react/components/Gap";
import { Button } from "js/Components/Button";
import { t } from "js/i18n";

const logger = getLogger();

const FolderNameContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    font-weight: 600;
`;

const StyledSkeleton = styled(Skeleton)`
    &&& {
        height: 34px;
    }
`;

const FolderIconButtonContainer = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 60px;
`;

const JoinFolderButtonContainer = styled.div.attrs<{ visible: boolean }>(({ visible }) => ({
    style: {
        opacity: visible ? 1 : 0
    }
})) < { visible: boolean }>`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 120px;
    margin-left: -60px;
    transition: opacity 0.3s ease-in-out;
`;

const StyledIconButton = styled(IconButton)`
    &&& {
        transition: opacity 0.3s ease-in-out;
        opacity: ${props => props.visible ? 1 : 0};
        width: 40px;
        height: 40px;

        &:hover {
            background: rgba(0, 0, 0, 0.03);
            border-radius: 50%;
        }
    }
`;

const PopupMenuItem = styled(MenuItem)`
    &&& {
        > .MuiIcon-root {
            margin-right: 5px;
            color: #666;
        }
    }
`;

const NoMembershipDivider = styled.div`
    font-weight: 600;
    color: #666;
    width: 100%;
    text-align: center;
`;

function FolderIconButton({ icon, visible, onClick }: { icon: string, visible: boolean, onClick?: () => void }) {
    return <StyledIconButton icon={icon} onClick={onClick} size={30} color="rgb(102,102,102)" visible={visible} />;
}

const Folder = WorkspaceController.withInitializedState(
    function Folder({ folder, resourceFolders }: { folder: IWorkspaceResourceFolder } & WorkspaceControllerState) {
        if (folder.parentFolderId) {
            return null;
        }
        const containerRef = useRef<HTMLDivElement>(null);
        const metadataLoadedAtRef = useRef<number>(0);

        const [isMouseOver, setIsMouseOver] = useState(false);
        const [fetching, setFetching] = useState(true);
        const [metadata, setMetadata] = useState<IWorkspaceResourceFolderMetadata | null>(null);
        const [permissions, setPermissions] = useState<IWorkspacePermissionAccess>(GET_DEFAULT_RESTRICTED_PERMISSIONS_ACCESS());
        const isIntersecting = useIntersectionObserver(containerRef);

        const hasDirectMembership = resourceFolders.some(f => f.id === folder.id);

        const isDefault = folder.folderType === WorkspaceResourceFolderType.SHARED_DEFAULT;
        const membersCount = metadata ? Object.values(metadata.counters.users).reduce((acc, curr) => acc + curr, 0) : 0;
        const presentationsCount = metadata ? metadata.counters.resources[WorkspaceResourceType.PRESENTATION] : 0;
        const canUpgradeToManageResourceFolders = WorkspaceController.canUpgradeToRemoveActionRestriction(ActionPermissionsObject.SHARED_RESOURCES, PermissionActionType.MANAGE);

        useEffect(() => {
            if (!isIntersecting) {
                return;
            }

            if (!metadata || folder.modifiedAt > metadataLoadedAtRef.current) {
                (async () => {
                    try {
                        const [metadata, permissions] = await Promise.all([
                            workspacesApi.getWorkspaceResourceFolderMetadata({
                                workspaceId: folder.workspaceId,
                                resourceFolderId: folder.id
                            }),
                            getUserPermissionsToResourceFolder({
                                workspaceId: folder.workspaceId,
                                folderId: folder.id,
                                includeImplicitPermissions: true
                            })
                        ]);
                        metadataLoadedAtRef.current = Date.now();
                        setMetadata(metadata);
                        setPermissions(permissions);
                        setFetching(false);
                    } catch (err) {
                        logger.error(err, `Failed to fetch data for folder ${folder.id}`);
                    }
                })();
            }
        }, [isIntersecting, folder.modifiedAt, hasDirectMembership]);

        const handleMouseEnter = () => setIsMouseOver(true);
        const handleMouseLeave = () => setIsMouseOver(false);

        const handleOpenAllTeamMembersFolderSettings = () => {
            ShowDialog(AllTeamMembersFolderSettingsDialog, { folderId: folder.id });
        };

        const handleShareFolder = (closePopup: () => void) => async () => {
            closePopup();

            ShowDialog(ManageTeamFolderMembersDialog, {
                folderId: folder.id
            });
        };

        const handleRenameFolder = closePopup => async () => {
            closePopup();

            const name = await ShowInputDialog({
                title: t("Rename Team Folder"),
                maxChars: 50,
                value: folder.name
            });

            if (!name || name === folder.name) {
                return;
            }

            try {
                await withProgressDialog({
                    title: t("Renaming folder..."),
                    action: () => WorkspaceController.updateWorkspaceResourceFolder({ folderId: folder.id, name })
                });
            } catch (err) {
                logger.error(err, `Failed to rename folder ${folder.id}`);

                ShowErrorDialog({
                    title: t("Failed to rename folder"),
                    message: t("An error occurred while renaming the folder. Please try again.")
                });
            }
        };

        const handleDeleteFolder = closePopup => async () => {
            closePopup();

            const shouldDelete = await ShowConfirmationDialog({
                title: t("Are you sure you want to delete this team folder?"),
                message: t("This action can not be undone.")
            });

            if (!shouldDelete) {
                return;
            }

            try {
                await withProgressDialog({
                    title: t("Deleting folder..."),
                    action: () => WorkspaceController.deleteWorkspaceResourceFolder({ folderId: folder.id })
                });
            } catch (err) {
                logger.error(err, `Failed to delete folder ${folder.id}`);

                ShowErrorDialog({
                    title: t("Failed to delete folder"),
                    message: t("An error occurred while deleting the folder. Please try again.")
                });
            }
        };

        const handleDoubleClick = () => {
            if (fetching || !permissions.write.object || !permissions.write.permissions || canUpgradeToManageResourceFolders) {
                return;
            }

            if (isDefault) {
                handleOpenAllTeamMembersFolderSettings();
                return;
            }

            handleShareFolder(() => { })();
        };

        const handleJoinFolder = async () => {
            try {
                await withProgressDialog({
                    title: t("Joining folder..."),
                    message: t("Please wait while we're adding you to the folder..."),
                    action: async () => {
                        await WorkspaceController.updateWorkspaceResourceFolderUsers({
                            folderId: folder.id,
                            update: {
                                add: [
                                    {
                                        subjectId: auth().currentUser.uid,
                                        subjectType: WorkspacePermissionSubjectType.USER
                                    }
                                ]
                            }
                        });

                        // Trigger so the folders gets added to the user's folder list
                        await WorkspaceController.forceRefreshWorkspaceResourceFolders([folder.id]);
                    }
                });
            } catch (err) {
                logger.error(err, "[Folder] Failed to join folder");

                ShowErrorDialog({
                    title: t("Failed to join folder"),
                    message: t("Please try again later.")
                });
            }
        };

        return (
            <ListLine
                ref={containerRef}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                onDoubleClick={handleDoubleClick}
                data-test-id={`team-folder-container-${folder.id}`}
                data-test-name={folder.name}
            >
                <FolderNameContainer data-test-id="team-folder-name-container">
                    <Icon>{isDefault ? "apartment" : "folder_open"}</Icon>
                    {folder.name}
                </FolderNameContainer>
                {fetching ? <StyledSkeleton /> : (
                    <div data-test-id="team-folder-members-count">
                        {membersCount === 1
                            ? t("{{count}} person", { count: membersCount })
                            : t("{{count}} people", { count: membersCount })}
                    </div>
                )}
                {fetching ? <StyledSkeleton /> : (
                    <div data-test-id="team-folder-presentations-count">
                        {presentationsCount === 1
                            ? t("{{count}} presentation", { count: presentationsCount })
                            : t("{{count}} presentations", { count: presentationsCount })}
                    </div>
                )}
                {!fetching && hasDirectMembership &&
                    <FolderIconButtonContainer data-test-id="team-folder-icon-button-container">
                        {isDefault && permissions.write.permissions &&
                            <FolderIconButton icon="settings" visible={isMouseOver} onClick={handleOpenAllTeamMembersFolderSettings} />
                        }
                        {!isDefault && (permissions.write.object || permissions.write.permissions || permissions.owner) &&
                            <Popup>
                                <PopupPreview>
                                    <FolderIconButton icon="more_vert" visible={isMouseOver} data-test-id="team-folder-more-vert-button" />
                                </PopupPreview>
                                <PopupContent>
                                    {(closePopup: () => void) => (
                                        canUpgradeToManageResourceFolders ? (
                                            <>
                                                {permissions.write.permissions && <PopupMenuItem disabled divider onClick={handleShareFolder(closePopup)}>
                                                    <UpgradeIcon /><Gap5 />{t("Share Folder...")}
                                                </PopupMenuItem>}
                                                {permissions.write.object && <PopupMenuItem disabled divider onClick={handleRenameFolder(closePopup)}>
                                                    <UpgradeIcon /><Gap5 />{t("Rename...")}
                                                </PopupMenuItem>}
                                                {permissions.owner && <PopupMenuItem disabled onClick={handleDeleteFolder(closePopup)}>
                                                    <UpgradeIcon /><Gap5 />{t("Delete Folder")}
                                                </PopupMenuItem>}
                                            </>
                                        ) : (
                                            <>
                                                {permissions.write.permissions && <PopupMenuItem divider onClick={handleShareFolder(closePopup)}>
                                                    <Icon>edit</Icon>
                                                    {t("Share Folder...")}
                                                </PopupMenuItem>}
                                                {permissions.write.object && <PopupMenuItem divider onClick={handleRenameFolder(closePopup)}>
                                                    <Icon>label</Icon>
                                                    {t("Rename...")}
                                                </PopupMenuItem>}
                                                {permissions.owner && <PopupMenuItem onClick={handleDeleteFolder(closePopup)}>
                                                    <Icon>delete</Icon>
                                                    {t("Delete Folder")}
                                                </PopupMenuItem>}
                                            </>
                                        )
                                    )}
                                </PopupContent>
                            </Popup>
                        }
                    </FolderIconButtonContainer>
                }
                {!fetching && !hasDirectMembership && permissions.write.permissions &&
                    <JoinFolderButtonContainer visible={isMouseOver}>
                        <Button onClick={handleJoinFolder}>{t("Join folder")}</Button>
                    </JoinFolderButtonContainer>
                }
            </ListLine>
        );
    }
);

function TeamFoldersPane({ resourceFolders }: WorkspaceControllerState) {
    const [allResourceFolders, setAllResourceFolders] = useState<IWorkspaceResourceFolder[]>([]);
    const [searchTerm, setSearchTerm] = useState("");

    const canUseResourceFolders = WorkspaceController.actionPermissions[ActionPermissionsObject.SHARED_RESOURCES].use;
    const canUpgradeToCreateSharedResources = (WorkspaceController.plan.tier !== WorkspacePlanTier.BASIC) && WorkspaceController.canUpgradeToRemoveActionRestriction(ActionPermissionsObject.SHARED_RESOURCES, PermissionActionType.CREATE);
    const canUpgradeToManageResourceFolders = WorkspaceController.canUpgradeToRemoveActionRestriction(ActionPermissionsObject.SHARED_RESOURCES, PermissionActionType.MANAGE);

    useEffect(() => {
        (async () => {
            try {
                const resourceFolders = await workspacesApi.getWorkspaceResourceFolders({
                    workspaceId: WorkspaceController.workspaceId,
                    includeImplicitPermissions: true
                });
                setAllResourceFolders(resourceFolders.filter(folder => !folder.parentFolderId && folder.folderType === WorkspaceResourceFolderType.SHARED));
            } catch (err) {
                logger.error(err, "[TeamFoldersPane] Failed to fetch all resource folders");
            }
        })();
    }, []);

    const handleSearch = (nextSearchTerm: string) => {
        setSearchTerm(nextSearchTerm?.trim().toLowerCase() ?? "");
    };

    const handleCreateFolder = async () => {
        const name = await ShowInputDialog({
            title: t("Create Team Folder"),
            maxChars: 50
        });

        if (!name) {
            if (name === "") {
                // @ts-ignore
                ShowWarningDialog({
                    title: t("Unable to create folder"),
                    message: t("Please enter a folder name."),
                });
            }
            return;
        }

        try {
            await withProgressDialog({
                title: t("Creating folder..."),
                action: async () => {
                    const { id } = await WorkspaceController.createWorkspaceResourceFolder({ name, resourcesType: WorkspaceResourceType.PRESENTATION, folderType: WorkspaceResourceFolderType.SHARED });
                    ShowDialog(ManageTeamFolderMembersDialog, {
                        folderId: id
                    });
                }
            });
        } catch (err) {
            logger.error(err, "Failed to create team folder");

            ShowErrorDialog({
                title: t("Failed to create team folder"),
                message: t("An error occurred while creating the folder. Please try again.")
            });
        }
    };

    const allTeamMembersFolder = resourceFolders.find(f => f.folderType === WorkspaceResourceFolderType.SHARED_DEFAULT);
    const teamFolders = resourceFolders.filter(folder => !folder.parentFolderId && folder.folderType === WorkspaceResourceFolderType.SHARED)
        .sort((a, b) => a.name.localeCompare(b.name));

    const foldersWhereUserHasNoMembership = allResourceFolders.filter(folder => !resourceFolders.some(f => f.id === folder.id));

    const displayedFolders = [allTeamMembersFolder, ...teamFolders]
        .filter(folder => folder && !(canUpgradeToManageResourceFolders && folder.folderType === WorkspaceResourceFolderType.SHARED_DEFAULT && teamFolders.length === 0));

    const filteredDisplayedFolders = displayedFolders.filter(folder =>
        !searchTerm || folder.name?.toLowerCase().includes(searchTerm)
    );
    const filteredFoldersWhereUserHasNoMembership = foldersWhereUserHasNoMembership.filter(folder =>
        !searchTerm || folder.name?.toLowerCase().includes(searchTerm)
    );
    const hasNoFolders = displayedFolders.length === 0 && foldersWhereUserHasNoMembership.length === 0;
    const hasNoMatch = !hasNoFolders && filteredDisplayedFolders.length === 0 && filteredFoldersWhereUserHasNoMembership.length === 0;

    return (
        <TeamResourcesUIPane>
            {(canUseResourceFolders || canUpgradeToManageResourceFolders) && (
                <TeamResourcesPaneHeader
                    title={t("Team Folders")}
                    action={canUseResourceFolders && !canUpgradeToCreateSharedResources ? (
                        <BlueButton onClick={handleCreateFolder} data-test-id="team-folders-create-folder-button">
                            {t("Create Team Folder")}
                        </BlueButton>
                    ) : null}
                    onSearch={canUseResourceFolders ? handleSearch : null}
                    searchPlaceholder={canUseResourceFolders ? t("Search team folders") : ""}
                />
            )}
            {canUpgradeToManageResourceFolders && (
                <FlexBox center fillWidth padding="0px 0px 20px 0px">
                    <Banner
                        headerText={t("Unlock Team Folders")}
                        bodyText={t("Upgrade to a Team plan to collaborate seamlessly and keep your team’s presentations organized and easy to find.")}
                        buttonLabel={t("Upgrade")}
                        onButtonClick={() => ShowDialog(ChangePlanDialog,
                            {
                                featuredPlan: WorkspacePlanTier.TEAM,
                                analytics: { cta: "team_folders" }
                            })}
                    />
                </FlexBox>
            )}
            <TeamResourcesResultsContainer $hasTopSpacing={canUpgradeToManageResourceFolders}>
                {!hasNoFolders && ( 
                    <List gridTemplateColumns="2fr 1fr 1fr 50px">
                      <ListHeaderLine>
                          <div>{t("Folder")}</div>
                          <div>{t("Members")}</div>
                          <div>{t("Presentations")}</div>
                      </ListHeaderLine>
                      {filteredDisplayedFolders.map(folder => (
                          <Folder key={folder.id} folder={folder} />
                      ))}
                      {filteredFoldersWhereUserHasNoMembership.length > 0 && <NoMembershipDivider>{t("Folders where you are not a member (view only)")}</NoMembershipDivider>}
                      {filteredFoldersWhereUserHasNoMembership.map(folder => (
                          <Folder key={folder.id} folder={folder} />
                      ))}
                  </List>
                )}
                {hasNoFolders && <NoTeamFoldersNotice />}
                {hasNoMatch && <NoMatchNotice />}
            </TeamResourcesResultsContainer>
        </TeamResourcesUIPane>
    );
}

export default WorkspaceController.withInitializedState(TeamFoldersPane);
